import { useEffect, useState } from "react";
import { SlideUpModal } from "src/components/SlideUpModal";
import { useWebClientApi } from "src/api";
import {
  toApplicationSummaryUpdateReq,
  type LoanOverviewFormData,
} from "src/api/adapters";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { safeNumber } from "src/utils";
import { type LoanOverviewDetail } from "./LoanOverviewPage";
import { getLoanOverviewFormErrors } from "./EditLoanDetail.validator";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import { Input } from "@porchpass/ui";

type GenericEditLoanDetailModalProps = {
  loanOverviewFormData: LoanOverviewFormData;
  editModalState?: LoanOverviewDetail;
  closeHandler: () => void;
  confirmHandler: () => void;
};

export default ({
  editModalState,
  loanOverviewFormData,
  closeHandler,
  confirmHandler,
}: GenericEditLoanDetailModalProps) => {
  if (!editModalState) return null;

  const { label, id, type } = editModalState;

  const api = useWebClientApi();
  const [formData, setFormData] = useState(loanOverviewFormData);
  useEffect(() => {
    setFormData(loanOverviewFormData);
  }, [editModalState?.id, loanOverviewFormData]);

  const updateField =
    (field: keyof LoanOverviewFormData) => (value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: LoanOverviewFormData) =>
      api.updateApplicationSummary(toApplicationSummaryUpdateReq(data)),
    );
  const formErrors = getLoanOverviewFormErrors(formData);
  const fieldError = fieldErrorsAfterSubmit(
    { value: formErrors[id]?.message },
    hasAttemptedFormSubmission,
  ).value;

  const onChangeHandler = (value: string) => {
    if (type === "percent") {
      return updateField(id)(String(safeNumber(value) / 100));
    }
    return updateField(id)(String(value));
  };

  useEffect(() => {
    if (response) {
      confirmHandler();
    }
  }, [response]);

  return formData[id] !== null ? (
    <SlideUpModal
      hasIcon
      title={`Edit ${label}`}
      isOpen={!!editModalState}
      actionButtonClickHandler={() => void submitHandler(formData, formErrors)}
      actionButtonText="Save"
      closeHandler={closeHandler}
      isLoading={loading}
    >
      <Input
        label={label ?? ""}
        inputMode={type === "percent" ? "decimal" : "numeric"}
        value={
          type === "percent"
            ? String(Math.round(safeNumber(formData[id]) * 100 * 100) / 100)
            : String(formData[id] ?? "")
        }
        onChange={(event) => onChangeHandler(event.target.value)}
        error={!!fieldError}
        helperText={fieldError}
        rightSlot={
          type === "percent" ? (
            <span className="text-xs text-on-surface-muted">%</span>
          ) : undefined
        }
      />
    </SlideUpModal>
  ) : null;
};
